import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return "Something went wrong.";
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

const Login = ({ onToken }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await axios.post(`${API}/login`, { password: pw });
      sessionStorage.setItem("mmAdmin", data.token);
      onToken(data.token);
    } catch (e2) {
      setErr(errText(e2));
    }
  };
  return (
    <form className="admin-login" onSubmit={submit} data-testid="admin-login-form">
      <div className="label">Modern Mind</div>
      <h1 className="gate-title" style={{ fontSize: 56 }}>Protocols</h1>
      <input type="password" className="line-input text-center" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="admin-password-input" />
      <button type="submit" className="mono-btn mt-6" data-testid="admin-login-btn">Enter</button>
      <div className="save-note" data-testid="admin-login-error">{err}</div>
    </form>
  );
};

const Entry = ({ row, i }) => {
  const [open, setOpen] = useState(false);
  const p = row.protocol;
  return (
    <div className="admin-row" data-testid={`admin-row-${i}`}>
      <button className={`admin-row-head ${p ? "" : "no-proto"}`} onClick={() => p && setOpen(!open)} data-testid={`admin-row-toggle-${i}`}>
        <span className="admin-email">{row.email}</span>
        <span className="admin-meta">{p ? `protocol saved ${fmt(row.updated)}` : "no protocol yet"}</span>
        <span className="admin-meta">signed up {fmt(row.signed_up)}</span>
      </button>
      {open && p && (
        <div className="admin-detail" data-testid={`admin-detail-${i}`}>
          <h4>Manifesting</h4>
          {(p.desires || []).map((d, j) => <p key={j}>{d.x} <em>to feel {d.y}</em></p>)}
          <h4>Who they are becoming</h4>
          <p>{p.identity}</p>
          {(p.groups || []).map((g, j) => <p key={j}><strong>{g.desire}:</strong> {(g.priorities || []).join("; ")}</p>)}
          <h4>Glimmers</h4>
          {Object.entries(p.glimmers || {}).map(([k, v]) => <p key={k}><em>{k}</em>: {v}</p>)}
          {p.howText && <p><strong>Celebrate:</strong> {p.howText}</p>}
          <h4>Limiting beliefs and fearless actions</h4>
          {(p.fears || []).map((f, j) => <p key={j}>{f.fear} <strong>&rarr;</strong> {f.step}</p>)}
          {p.scheduled?.action && <p><strong>Scheduled:</strong> {p.scheduled.action} ({p.scheduled.date} {p.scheduled.time})</p>}
        </div>
      )}
    </div>
  );
};

const ChangePassword = ({ auth }) => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [msg, setMsg] = useState("");
  const save = async () => {
    try {
      await axios.post(`${API}/password`, { current: cur, new: nw }, auth);
      setMsg("Password updated.");
      setCur("");
      setNw("");
    } catch (e) {
      setMsg(errText(e));
    }
  };
  return (
    <div className="admin-pw" data-testid="admin-change-password">
      <input type="password" className="line-input" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" data-testid="admin-current-password" />
      <input type="password" className="line-input" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (8+ characters)" data-testid="admin-new-password" />
      <button className="mono-btn calendar-btn" onClick={save} data-testid="admin-save-password-btn">Change password</button>
      <div className="save-note" data-testid="admin-password-note">{msg}</div>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("mmAdmin"));
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showPw, setShowPw] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const logout = () => {
    sessionStorage.removeItem("mmAdmin");
    setToken(null);
    setRows(null);
    setShowPw(false);
  };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/protocols`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setRows(r.data)).catch(() => logout());
  }, [token]);

  const [exportErr, setExportErr] = useState("");
  const exportCsv = async () => {
    setExportErr("");
    try {
      const r = await axios.get(`${API}/export.csv`, { ...auth, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `modern-mind-protocols-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else setExportErr("Export failed, please try again.");
    }
  };

  if (!token) return <div className="mm admin-page"><Login onToken={setToken} /></div>;
  const list = (rows || []).filter((r) => r.email.includes(q.trim().toLowerCase()));
  const withProto = (rows || []).filter((r) => r.protocol).length;

  return (
    <div className="mm admin-page" data-testid="admin-dashboard">
      <div className="admin-wrap">
        <div className="admin-top">
          <div>
            <div className="label">Modern Mind</div>
            <h1 className="admin-title">Protocols</h1>
            <p className="admin-count" data-testid="admin-count">{rows ? `${rows.length} visitors, ${withProto} protocols` : "Loading..."}</p>
          </div>
          <div className="btn-row">
            <button className="mono-btn" onClick={exportCsv} data-testid="admin-export-btn">Export CSV</button>
            <button className="text-btn" onClick={() => setShowPw(!showPw)} data-testid="admin-toggle-password">Password</button>
            <button className="text-btn" onClick={logout} data-testid="admin-logout-btn">Log out</button>
          </div>
        </div>
        <div className="save-note" data-testid="admin-export-error">{exportErr}</div>
        {showPw && <ChangePassword auth={auth} />}
        <input className="line-input admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email" data-testid="admin-search-input" />
        <div className="admin-list">
          {list.map((r, i) => <Entry key={r.email} row={r} i={i} />)}
          {rows && list.length === 0 && <p className="admin-meta" data-testid="admin-empty">No visitors yet.</p>}
        </div>
      </div>
    </div>
  );
}
